import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError, downloadBlob } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Empty, AccessBadge } from "../components/ui-helpers";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { RequirementHover } from "../components/RequirementHover";
import { Plus, Trash, DownloadSimple, Paperclip, LinkSimple, PencilSimple } from "@phosphor-icons/react";

const TYPES = ["Document", "Screenshot", "Log Export", "Report", "Record", "Configuration", "Attestation"];

export default function Evidence() {
  const [items, setItems] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [controls, setControls] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", file_name: "", evidence_type: "Document", period_start: "", period_end: "", linked_requirements: [], linked_controls: [], linked_parts: [] });
  const [file, setFile] = useState(null);
  const [source, setSource] = useState("upload"); // upload | link
  const [linkUrl, setLinkUrl] = useState("");
  const [reqFilter, setReqFilter] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const { canEdit, isRole } = useAuth();
  const mayUpload = isRole("admin", "analyst", "reviewer");

  const load = useCallback(() => api.get("/evidence").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); api.get("/requirements").then((r) => setReqs(r.data)); api.get("/controls").then((r) => setControls(r.data)); }, [load]);

  const toggle = (key, id) => setForm((f) => ({ ...f, [key]: f[key].includes(id) ? f[key].filter((x) => x !== id) : [...f[key], id] }));

  // Linking a Part auto-links its parent requirement (one evidence record, many connectors — no duplication).
  const isPartLinked = (rid, label) => form.linked_parts.some((p) => p.requirement_id === rid && p.label === label);
  const togglePart = (rid, label) => setForm((f) => {
    const exists = f.linked_parts.some((p) => p.requirement_id === rid && p.label === label);
    const linked_parts = exists ? f.linked_parts.filter((p) => !(p.requirement_id === rid && p.label === label))
                                : [...f.linked_parts, { requirement_id: rid, label }];
    const linked_requirements = (!exists && !f.linked_requirements.includes(rid)) ? [...f.linked_requirements, rid] : f.linked_requirements;
    return { ...f, linked_parts, linked_requirements };
  });
  // Unchecking a requirement removes its part connectors too.
  const toggleReq = (rid) => setForm((f) => {
    const has = f.linked_requirements.includes(rid);
    return {
      ...f,
      linked_requirements: has ? f.linked_requirements.filter((x) => x !== rid) : [...f.linked_requirements, rid],
      linked_parts: has ? f.linked_parts.filter((p) => p.requirement_id !== rid) : f.linked_parts,
    };
  });
  // Select / clear ALL requirements in a single standard at once (e.g. an SOP covering the entire standard).
  // Selecting the whole standard links each requirement at the requirement level (no sub-part = applies to whole requirement).
  const toggleStandard = (ids) => setForm((f) => {
    const allSelected = ids.length > 0 && ids.every((id) => f.linked_requirements.includes(id));
    if (allSelected) {
      return {
        ...f,
        linked_requirements: f.linked_requirements.filter((x) => !ids.includes(x)),
        linked_parts: f.linked_parts.filter((p) => !ids.includes(p.requirement_id)),
      };
    }
    return { ...f, linked_requirements: Array.from(new Set([...f.linked_requirements, ...ids])) };
  });
  // Select / clear an ENTIRE standard INCLUDING every sub-part of every requirement (fan-out for an SOP that covers everything).
  const toggleStandardParts = (grp) => setForm((f) => {
    const ids = grp.map((r) => r.id);
    const allParts = grp.flatMap((r) => (r.sub_requirements || []).map((sp) => ({ requirement_id: r.id, label: sp.label })));
    const key = (p) => `${p.requirement_id}|${p.label}`;
    const partKeys = new Set(f.linked_parts.map(key));
    const fully = ids.every((id) => f.linked_requirements.includes(id)) && allParts.every((ap) => partKeys.has(key(ap)));
    if (fully) {
      return {
        ...f,
        linked_requirements: f.linked_requirements.filter((x) => !ids.includes(x)),
        linked_parts: f.linked_parts.filter((p) => !ids.includes(p.requirement_id)),
      };
    }
    const newParts = allParts.filter((ap) => !partKeys.has(key(ap)));
    return {
      ...f,
      linked_requirements: Array.from(new Set([...f.linked_requirements, ...ids])),
      linked_parts: [...f.linked_parts, ...newParts],
    };
  });

  const resetForm = () => {
    setForm({ name: "", description: "", file_name: "", evidence_type: "Document", period_start: "", period_end: "", linked_requirements: [], linked_controls: [], linked_parts: [] });
    setFile(null); setSource("upload"); setLinkUrl(""); setReqFilter("");
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    if (source === "link" && !/^https?:\/\//i.test(linkUrl.trim())) return toast.error("Enter a valid http(s):// repository link");
    const fd = new FormData();
    fd.append("name", form.name); fd.append("description", form.description); fd.append("evidence_type", form.evidence_type);
    if (form.file_name) fd.append("file_name", form.file_name);
    if (form.period_start) fd.append("period_start", form.period_start);
    if (form.period_end) fd.append("period_end", form.period_end);
    fd.append("linked_requirements", JSON.stringify(form.linked_requirements));
    fd.append("linked_controls", JSON.stringify(form.linked_controls));
    fd.append("linked_parts", JSON.stringify(form.linked_parts));
    fd.append("source_type", source);
    if (source === "link") fd.append("link_url", linkUrl.trim());
    else if (file) fd.append("file", file);
    try {
      await api.post("/evidence", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setOpen(false); resetForm();
      load(); toast.success("Evidence added");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => { await api.delete(`/evidence/${id}`); load(); };

  const openEdit = (e) => {
    setForm({
      name: e.name || "", description: e.description || "", file_name: e.file_name || "",
      evidence_type: e.evidence_type || "Document", period_start: e.period_start || "", period_end: e.period_end || "",
      linked_requirements: e.linked_requirements || [], linked_controls: e.linked_controls || [], linked_parts: e.linked_parts || [],
    });
    setReqFilter(""); setEditId(e.id); setEditOpen(true);
  };
  const saveEdit = async () => {
    try {
      await api.patch(`/evidence/${editId}`, {
        name: form.name, description: form.description,
        linked_requirements: form.linked_requirements, linked_controls: form.linked_controls, linked_parts: form.linked_parts,
      });
      setEditOpen(false); setEditId(null); resetForm();
      load(); toast.success("Evidence links updated");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const reqLabel = (rid) => { const r = reqs.find((x) => x.id === rid); return r ? `${r.standard_id} ${r.req_number}` : "?"; };
  const reqText = (rid) => { const r = reqs.find((x) => x.id === rid); return r ? r.text : ""; };
  const download = async (id) => {
    try { await downloadBlob(`/evidence/${id}/download`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const HoverText = RequirementHover;

  const mappingPicker = (
    <div className="grid grid-cols-2 gap-3">
      <div><Label>Map to Requirements & Parts</Label>
        <Input data-testid="ev-req-filter" value={reqFilter} onChange={(e) => setReqFilter(e.target.value)}
          placeholder="Filter by standard / requirement…" className="rounded-none mt-1 h-8 text-xs" />
        <div className="border border-border mt-1 max-h-60 overflow-y-auto p-2 space-y-3">
          {(() => {
            const filtered = reqs.filter((r) => !reqFilter || `${r.standard_id} ${r.req_number} ${r.text || ""}`.toLowerCase().includes(reqFilter.toLowerCase()));
            const groups = {};
            filtered.forEach((r) => { (groups[r.standard_id] = groups[r.standard_id] || []).push(r); });
            const standardIds = Object.keys(groups).sort();
            if (standardIds.length === 0) return <p className="text-xs text-muted-foreground">No requirements match.</p>;
            return standardIds.map((sid) => {
              const grp = groups[sid];
              const ids = grp.filter((r) => !r.access?.gated).map((r) => r.id);
              const allSelected = ids.length > 0 && ids.every((id) => form.linked_requirements.includes(id));
              const someSelected = ids.some((id) => form.linked_requirements.includes(id));
              const hasParts = grp.some((r) => !r.access?.gated && (r.sub_requirements || []).length > 0);
              const allParts = grp.filter((r) => !r.access?.gated).flatMap((r) => (r.sub_requirements || []).map((sp) => `${r.id}|${sp.label}`));
              const linkedPartKeys = new Set(form.linked_parts.map((p) => `${p.requirement_id}|${p.label}`));
              const fullyWithParts = allSelected && allParts.every((k) => linkedPartKeys.has(k));
              return (
                <div key={sid} data-testid={`ev-standard-group-${sid}`}>
                  <div className="flex items-center justify-between gap-2 sticky top-0 bg-card py-1 border-b border-border mb-1">
                    <span className="font-mono font-bold text-xs">{sid} <span className="text-[10px] text-muted-foreground font-normal">({grp.length} req{grp.length === 1 ? "" : "s"}{someSelected ? `, ${ids.filter((id) => form.linked_requirements.includes(id)).length} linked` : ""})</span></span>
                    <div className="flex items-center gap-1 shrink-0">
                      <button type="button" data-testid={`ev-select-standard-${sid}`} onClick={() => toggleStandard(ids)}
                        className={`text-[10px] font-semibold px-2 py-0.5 border ${allSelected ? "bg-primary text-primary-foreground border-black" : "bg-card border-border hover:bg-secondary"}`}>
                        {allSelected ? "Clear all" : "Select standard"}
                      </button>
                      {hasParts && (
                        <button type="button" data-testid={`ev-select-standard-parts-${sid}`} onClick={() => toggleStandardParts(grp.filter((r) => !r.access?.gated))}
                          className={`text-[10px] font-semibold px-2 py-0.5 border ${fullyWithParts ? "bg-primary text-primary-foreground border-black" : "bg-card border-border hover:bg-secondary"}`}>
                          {fullyWithParts ? "Clear all + parts" : "Select all + parts"}
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="space-y-1">
                    {grp.map((r) => {
                      const reqGated = r.access?.gated;
                      return (
                      <div key={r.id} className={reqGated ? "opacity-50" : ""}>
                        <label className={`flex items-center gap-2 text-xs ${reqGated ? "cursor-not-allowed" : "cursor-pointer"}`}>
                          <Checkbox data-testid={`ev-req-${r.req_number}`} disabled={reqGated} checked={!reqGated && form.linked_requirements.includes(r.id)} onCheckedChange={() => !reqGated && toggleReq(r.id)} className="rounded-none" />
                          <HoverText label={`${r.standard_id} ${r.req_number}`} text={r.text}>
                            <span data-testid={`ev-req-hover-${r.req_number}`} className="font-mono font-bold underline decoration-dotted decoration-muted-foreground/40 underline-offset-2">{r.standard_id} {r.req_number}</span>
                          </HoverText>
                          {(r.sub_requirements || []).length > 0 && <span className="text-[10px] text-muted-foreground">({r.sub_requirements.length} parts)</span>}
                          {reqGated && <AccessBadge access={r.access} data-testid={`ev-req-access-${r.req_number}`} />}
                        </label>
                        {!reqGated && form.linked_requirements.includes(r.id) && r.measure && (
                          <div className="ml-5 mt-1 border-l-2 border-emerald-400 dark:border-emerald-600 bg-emerald-50/60 dark:bg-emerald-950/40 pl-2 py-1" data-testid={`ev-req-measure-${r.req_number}`}>
                            <span className="font-bold uppercase text-[9px] text-emerald-700 dark:text-emerald-300 mr-1">Measure</span>
                            <span className="text-[11px] text-emerald-900">{r.measure}</span>
                          </div>
                        )}
                        {!reqGated && form.linked_requirements.includes(r.id) && (r.sub_requirements || []).length > 0 && (
                          <div className="ml-5 mt-1 mb-1 pl-2 border-l border-border space-y-1">
                            {r.sub_requirements.map((sp) => (
                              <label key={sp.label} className="flex items-start gap-2 text-[11px] cursor-pointer">
                                <Checkbox data-testid={`ev-part-${r.req_number}-${sp.label}`} checked={isPartLinked(r.id, sp.label)} onCheckedChange={() => togglePart(r.id, sp.label)} className="rounded-none mt-0.5" />
                                <HoverText label={`${r.standard_id} ${r.req_number} · ${sp.label}`} text={sp.text}>
                                  <span className="cursor-pointer"><span className="font-mono font-semibold underline decoration-dotted decoration-muted-foreground/40 underline-offset-2">{sp.label}</span> <span className="text-muted-foreground">{(sp.text || "").slice(0, 48)}</span></span>
                                </HoverText>
                                {sp.measure && <span title={sp.measure} className="text-[9px] uppercase font-bold text-emerald-700 border border-emerald-300 bg-emerald-50 px-1 shrink-0">M</span>}
                              </label>
                            ))}
                            <p className="text-[10px] text-muted-foreground italic">No part selected = applies to the whole requirement.</p>
                          </div>
                        )}
                      </div>
                      );
                    })}
                  </div>
                </div>
              );
            });
          })()}
        </div>
      </div>
      <div><Label>Map to Controls</Label>
        <div className="border border-border mt-1 max-h-32 overflow-y-auto p-2 space-y-1">
          {controls.map((c) => <label key={c.id} className="flex items-center gap-2 text-xs cursor-pointer"><Checkbox checked={form.linked_controls.includes(c.id)} onCheckedChange={() => toggle("linked_controls", c.id)} className="rounded-none" />{c.name}</label>)}
          {controls.length === 0 && <p className="text-xs text-muted-foreground">No controls yet.</p>}
        </div>
      </div>
    </div>
  );

  // Audit-readiness coverage: a standard is "fully covered" when every requirement in it has ≥1 evidence connector.
  const linkedReqIds = new Set(items.flatMap((e) => e.linked_requirements || []));
  const stdGroups = {};
  reqs.forEach((r) => { (stdGroups[r.standard_id] = stdGroups[r.standard_id] || []).push(r); });
  const totalStandards = Object.keys(stdGroups).length;
  let fullyCovered = 0, partiallyCovered = 0;
  Object.values(stdGroups).forEach((grp) => {
    const covered = grp.filter((r) => linkedReqIds.has(r.id)).length;
    if (covered === grp.length) fullyCovered += 1;
    else if (covered > 0) partiallyCovered += 1;
  });
  const reqsWithEvidence = reqs.filter((r) => linkedReqIds.has(r.id)).length;

  return (
    <div>
      <PageHeader title="Evidence Management" subtitle="Upload, tag, and map evidence to requirements & controls"
        action={mayUpload && <Button data-testid="new-evidence-btn" onClick={() => setOpen(true)} className="rounded-none bg-primary hover:bg-primary/90"><Plus size={16} className="mr-1" />Add Evidence</Button>} />
      <div className="p-8">
        {reqs.length > 0 && (
          <div data-testid="coverage-summary" className="grid grid-cols-3 gap-3 mb-6">
            <div className="border border-border bg-card p-4">
              <div className="overline text-muted-foreground text-xs">Standards fully covered</div>
              <div data-testid="coverage-fully" className="text-2xl font-bold mt-1">{fullyCovered}<span className="text-base font-normal text-muted-foreground"> / {totalStandards}</span></div>
              <div className="text-[11px] text-muted-foreground">every requirement has evidence</div>
            </div>
            <div className="border border-border bg-card p-4">
              <div className="overline text-muted-foreground text-xs">Standards partially covered</div>
              <div data-testid="coverage-partial" className="text-2xl font-bold mt-1">{partiallyCovered}</div>
              <div className="text-[11px] text-muted-foreground">some, but not all, requirements covered</div>
            </div>
            <div className="border border-border bg-card p-4">
              <div className="overline text-muted-foreground text-xs">Requirements with evidence</div>
              <div data-testid="coverage-reqs" className="text-2xl font-bold mt-1">{reqsWithEvidence}<span className="text-base font-normal text-muted-foreground"> / {reqs.length}</span></div>
              <div className="text-[11px] text-muted-foreground">across all evidence connectors</div>
            </div>
          </div>
        )}
        {items.length === 0 ? <Empty title="No evidence yet" hint="Upload evidence and link it to requirements." /> : (
          <div className="border border-border bg-card">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border bg-secondary/40 text-left overline text-muted-foreground">
                <th className="px-4 py-3">Name</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Period</th><th className="px-4 py-3">Mapped To</th><th className="px-4 py-3">File</th><th className="px-4 py-3"></th>
              </tr></thead>
              <tbody>
                {items.map((e) => (
                  <tr key={e.id} data-testid={`evidence-row-${e.id}`} className="border-b border-border hover:bg-secondary/40">
                    <td className="px-4 py-3 font-medium">{e.name}<div className="text-xs text-muted-foreground font-normal">{e.description}</div></td>
                    <td className="px-4 py-3">{e.evidence_type}</td>
                    <td className="px-4 py-3 font-mono text-xs">{e.period_start || "?"} → {e.period_end || "?"}</td>
                    <td className="px-4 py-3"><div className="flex flex-wrap gap-1">{e.linked_requirements.map((rid) => {
                      const parts = (e.linked_parts || []).filter((p) => p.requirement_id === rid).map((p) => p.label);
                      return <HoverText key={rid} label={reqLabel(rid)} text={reqText(rid)}>
                        <span data-testid={`mapped-badge-${e.id}-${rid}`} className="font-mono text-[11px] bg-primary text-primary-foreground px-1.5 py-0.5 cursor-help">{reqLabel(rid)}{parts.length > 0 && <span className="text-zinc-300"> · {parts.join(", ")}</span>}</span>
                      </HoverText>;
                    })}{e.linked_requirements.length === 0 && <span className="text-xs text-red-600">unmapped</span>}</div></td>
                    <td className="px-4 py-3">{e.source_type === "link" && e.link_url
                      ? <a data-testid={`evlink-${e.id}`} href={e.link_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-blue-600 hover:underline text-xs"><LinkSimple size={14} />Repository link</a>
                      : e.file ? <button data-testid={`download-${e.id}`} onClick={() => download(e.id)} className="flex items-center gap-1 text-blue-600 hover:underline text-xs"><DownloadSimple size={14} />{e.file.filename}</button>
                      : <span className="text-xs text-muted-foreground">none</span>}</td>
                    <td className="px-4 py-3"><div className="flex items-center gap-2">
                      {canEdit && <button data-testid={`edit-evidence-${e.id}`} onClick={() => openEdit(e)} title="Edit links" className="text-muted-foreground hover:text-foreground"><PencilSimple size={16} /></button>}
                      {canEdit && <button data-testid={`delete-evidence-${e.id}`} onClick={() => remove(e.id)} title="Delete" className="text-muted-foreground hover:text-red-600"><Trash size={16} /></button>}
                    </div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Add Evidence</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input data-testid="evidence-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none mt-1" /></div>
            <div><Label>What this evidence shows / how it demonstrates compliance</Label>
              <Textarea data-testid="evidence-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Describe what the artifact shows and how it proves the requirement is met. This feeds the RSAW narrative generator." className="rounded-none mt-1" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Type</Label>
                <Select value={form.evidence_type} onValueChange={(v) => setForm({ ...form, evidence_type: v })}>
                  <SelectTrigger data-testid="evidence-type-select" className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Period Start</Label><Input type="date" value={form.period_start} onChange={(e) => setForm({ ...form, period_start: e.target.value })} className="rounded-none mt-1" /></div>
              <div><Label>Period End</Label><Input type="date" value={form.period_end} onChange={(e) => setForm({ ...form, period_end: e.target.value })} className="rounded-none mt-1" /></div>
            </div>
            <div>
              <Label className="flex items-center gap-1"><Paperclip size={14} />Evidence source</Label>
              <div className="flex gap-2 mt-1 mb-2">
                <button type="button" data-testid="evidence-source-upload" onClick={() => setSource("upload")}
                  className={`px-3 py-1.5 text-xs font-semibold border ${source === "upload" ? "bg-primary text-primary-foreground border-black" : "bg-card border-border"}`}>Upload file</button>
                <button type="button" data-testid="evidence-source-link" onClick={() => setSource("link")}
                  className={`px-3 py-1.5 text-xs font-semibold border ${source === "link" ? "bg-primary text-primary-foreground border-black" : "bg-card border-border"}`}>Link to repository</button>
              </div>
              {source === "upload" ? (
                <Input data-testid="evidence-file-input" type="file" onChange={(e) => setFile(e.target.files[0])} className="rounded-none" />
              ) : (
                <>
                  <Input data-testid="evidence-link-input" type="url" value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)}
                    placeholder="https://yourcompany.sharepoint.com/…" className="rounded-none" />
                  <p className="text-[11px] text-muted-foreground mt-1">Paste a SharePoint, Google Drive, or other repository link. Reviewers open it in a new tab; nothing is uploaded to Claro.</p>
                  <div className="mt-2"><Label className="text-xs">Referenced file name <span className="text-muted-foreground font-normal">(optional)</span></Label>
                    <Input data-testid="evidence-file-name-input" value={form.file_name} onChange={(e) => setForm({ ...form, file_name: e.target.value })}
                      placeholder="e.g. CIP-007-R1_Port_Scan_Report_2026Q1.xlsx" className="rounded-none mt-1" />
                    <p className="text-[11px] text-muted-foreground mt-1">The name of the artifact in your repository — referenced by the RSAW narrative generator.</p>
                  </div>
                </>
              )}
            </div>
            {mappingPicker}
            <Button data-testid="save-evidence-btn" onClick={save} className="rounded-none w-full bg-primary hover:bg-primary/90">Save Evidence</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={(o) => { setEditOpen(o); if (!o) { setEditId(null); resetForm(); } }}>
        <DialogContent className="rounded-none max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit Evidence Links</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input data-testid="edit-evidence-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none mt-1" /></div>
            <div><Label>What this evidence shows / how it demonstrates compliance</Label>
              <Textarea data-testid="edit-evidence-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-none mt-1" /></div>
            <p className="text-xs text-muted-foreground">Add or remove the requirements, parts, and controls this evidence is linked to. The file/link itself isn't changed — one evidence record, many connectors.</p>
            {mappingPicker}
            <Button data-testid="save-evidence-links-btn" onClick={saveEdit} className="rounded-none w-full bg-primary hover:bg-primary/90">Save Links</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
